import { isString, camelCase, startsWith } from 'lodash';
import parse from 'url-parse';
import cache from 'memory-cache';
import { tryEach } from 'async';
import gradient from 'random-gradient';
import axios from 'axios';
import * as Vibrant from 'node-vibrant';

const config = {
  api: {
    logo: 'https://logo.clearbit.com/',
    autocomplete: 'https://autocomplete.clearbit.com/v1/companies/suggest',
  }
};

const getImagePalette = (url) => {
  return new Promise((resolve, reject) => {
    // url can start with `//`
    const logoUrl = startsWith(url, '//') ? `https:${url}` : url;
    Vibrant.from(logoUrl)
      .getPalette()
      .then(
        (response) => {
          const keys = Object.keys(response);
          const addPalette = (acc, paletteName) => ({
            ...acc,
            [camelCase(paletteName)]: response[paletteName] && response[paletteName].getHex(),
          });
          const colorPallete = keys.reduce(addPalette, {});
          resolve(colorPallete);
        },
        (error) => {
          reject(error);
        },
      );
  });
  
};

const loadWithRetry = (query) => {
  return new Promise((resolve, reject) => {
    tryEach([
      (callback) => {
        const params = {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          responseType: 'json',
        };
        const instance = axios.create(params);
        const url = `${config.api.autocomplete}?query=${query}`;
        instance.get(url)
          .then(
            (results) => {
              let logo = null;
              if (results.data && results.data.length) {
                [{ logo }] = results.data;
              }
              if (logo) {
                callback(null, logo);
              } else {
                callback({ status: 404 });
              }
            },
            (errors) => {
              callback(errors);
            },
          );
      },
      (callback) => {
        if (typeof window !== 'undefined') {
          const image = new window.Image();
          const logo = `//logo.clearbit.com/${query}`;
          image.onload = () => {
            callback(null, logo);
          };
          image.onerror = (error) => {
            callback(error);
          };
          image.src = logo;
        } else {
          callback({ status: 404 });
        }
      },
    ], (errors, results) => {
      if (errors) {
        reject(errors);
      } else {
        resolve(results);
      }
    });
  });
};

export const getPalleteFromId = (id) => {
  const bg = gradient(id); // linear-gradient(to top right, #f9e806, #06f9e8)
  const parts = bg.split(', ');
  const primaryColor = parts.length === 3 ? parts[1] : null;
  const secondaryColor = parts.length === 3 ? parts[2].replace(')', '') : null;
  return [primaryColor, secondaryColor];
}

const getLogo = (queryObject) => {
  const currentLocation = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
  let query = null;
  if (isString(queryObject)) {
    query = queryObject;
  } else {
    // { queryObject: { url, name } }
    query = queryObject.name;
    if (queryObject.url) {
      if (startsWith(queryObject.url, 'http')) {
        const parsed = parse(queryObject.url, true);
        query = parsed.hostname;
      } else {
        query = queryObject.url;
      }
      if (query === 'localhost' || query === currentLocation) {
        query = queryObject.name;
      }
    }
  }
  return new Promise((resolve, reject) => {
    if (cache.get(query)) {
      const {
        status, data, errors,
        colorPallete,
      } = cache.get(query);
      if (status === 200) {
        resolve({ logo: data, colorPallete });
      } else {
        reject(errors);
      }
    } else {
      loadWithRetry(query)
        .then(
          (logo) => {
            getImagePalette(logo)
              .then(
                (colorPallete) => {
                  cache.put(query, { status: 200, data: logo, colorPallete });
                  resolve({ logo, colorPallete });
                },
                () => {
                  const colorPallete = {};
                  if (queryObject.id) {
                    const [primaryColor, secondaryColor] = getPalleteFromId(queryObject.id);
                    if (primaryColor && secondaryColor) {
                      colorPallete.vibrant = primaryColor;
                      colorPallete.darkVibrant = secondaryColor;
                    }
                  }
                  cache.put(query, { status: 200, data: logo, colorPallete });
                  resolve({ logo, colorPallete });
                },
              );
          },
          (errors) => {
            cache.put(query, {
              status: errors.status, data: null, errors, colorPallete: {},
            });
            reject(errors);
          },
        );
    }
  });
};

export default getLogo;